'use client';

import { Button, DialogFooter } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Error, UploadFail } from '@shared/icons/system';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { Link } from '../../../../Link/Link';
import { useKycDialog } from '../context/KycDialogContext';

type ErrorStepProps = {
  onRetry: () => void;
  isUploadFailure?: boolean;
};

function ErrorStep({ onRetry, isUploadFailure = false }: ErrorStepProps) {
  const t = useTranslations('global_dialogs.kyc_verification.shared');
  const { isNameChangeFlow } = useKycDialog();
  const { isUseNameChangeManualUploadCopy } = getSiteConfig().PROFILE_INFO;
  const isNameChangeCopy = isUploadFailure && isNameChangeFlow && isUseNameChangeManualUploadCopy;
  const ErrorIcon = isNameChangeCopy ? UploadFail : Error;
  const copyPrefix = isNameChangeCopy ? 'name_change.' : '';

  return (
    <div className="w-full flex flex-col flex-1 self-center md:justify-center gap-8 pt-10 md:pt-6" data-testid="error-step" data-track-container="Upload Failure">
      <div className="flex flex-col gap-8 align-center flex-1">
        <div className="flex flex-col flex-1 gap-4">
          <ErrorIcon size="extra-extra-large" data-testid="kyc-error-icon" />

          <div className="flex flex-col">
            <h4 className="mb-2">{t(`${copyPrefix}error_title`)}</h4>
            <p className="paragraph leading-relaxed">{t(`${copyPrefix}error_description`)}</p>
            {isNameChangeCopy ? null : (
              <p className="paragraph text-foreground-subtle mt-10 mb-8 md:mt-5">
                {t.rich('help_message', {
                  link: chunks => (
                    <Link href={ROUTES.HELP_CENTRE.BASE_URL} target="_blank" rel="noopener" data-testid="help-link" data-track-name="Help">
                      {chunks}
                    </Link>
                  ),
                })}
              </p>
            )}
          </div>
        </div>

        <DialogFooter data-track-container="Actions">
          <Button type="button" variant="primary" className="w-full md:w-auto" onClick={onRetry} data-testid="retry-button" data-track-name="Retry">
            {t('retry_button')}
          </Button>
        </DialogFooter>
      </div>
    </div>
  );
}

export { ErrorStep };
